import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../contexts/UserContext';
import apiService from '../services/api';
import { calculateLevel } from '../utils/levelSystem';
import '../styles/Leaderboard.css';

const Leaderboard = ({ sidebarOpen }) => {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState('all-time');
  const [showFriendsOnly, setShowFriendsOnly] = useState(false);
  const [allTimeLeaderboard, setAllTimeLeaderboard] = useState([]);
  const [monthlyLeaderboard, setMonthlyLeaderboard] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userRank, setUserRank] = useState({ allTime: null, monthly: null });
  const [myFriends, setMyFriends] = useState([]);

  // Load friends data (same as Feed)
  const loadFriends = useCallback(async () => {
    if (!user) return;
    try {
      const response = await apiService.getFriends();
      if (response && response.success) {
        setMyFriends(response.friends || []);
        console.log('👥 Loaded friends for leaderboard:', response.friends?.length || 0);
      }
    } catch (error) {
      console.error('Failed to load friends:', error);
    }
  }, [user]);

  // Load leaderboard data
  const loadLeaderboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log(`🔄 Loading leaderboard - Friends only: ${showFriendsOnly}`);
      
      // Load all-time leaderboard
      const allTimeResponse = await apiService.getLeaderboard('all-time', 10, showFriendsOnly);
      console.log('📊 All-time response:', allTimeResponse);
      setAllTimeLeaderboard(allTimeResponse.leaderboard || []);
      setUserRank(prev => ({ ...prev, allTime: allTimeResponse.userRank }));

      // Load monthly leaderboard
      const monthlyResponse = await apiService.getLeaderboard('monthly', 10, showFriendsOnly);
      console.log('📅 Monthly response:', monthlyResponse);
      setMonthlyLeaderboard(monthlyResponse.leaderboard || []);
      setUserRank(prev => ({ ...prev, monthly: monthlyResponse.userRank }));
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setIsLoading(false);
    }
  }, [showFriendsOnly]);

  useEffect(() => {
    loadLeaderboardData();
    loadFriends();
  }, [loadLeaderboardData, loadFriends]);

  // Reload data when friends toggle changes
  useEffect(() => {
    loadLeaderboardData();
    if (showFriendsOnly) {
      loadFriends(); // Refresh friends list when switching to friends mode
    }
  }, [showFriendsOnly, loadLeaderboardData, loadFriends]);

  // Listen for stats refresh events to update leaderboard
  useEffect(() => {
    const handleStatsRefresh = () => {
      console.log('Stats refresh event received, reloading leaderboard...');
      loadLeaderboardData();
    };

    window.addEventListener('statsRefresh', handleStatsRefresh);
    
    return () => {
      window.removeEventListener('statsRefresh', handleStatsRefresh);
    };
  }, [loadLeaderboardData]);


  // Refresh leaderboard when page becomes visible (in case user switched tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page became visible, refreshing leaderboard...');
        loadLeaderboardData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadLeaderboardData]);

  const getCurrentLeaderboard = () => {
    return activeTab === 'all-time' ? allTimeLeaderboard : monthlyLeaderboard;
  };

  const getCurrentUserRank = () => {
    return activeTab === 'all-time' ? userRank.allTime : userRank.monthly;
  };

  const formatPoints = (points) => {
    if (points >= 1000000) {
      return (points / 1000000).toFixed(1) + 'M';
    } else if (points >= 1000) {
      return (points / 1000).toFixed(1) + 'K';
    }
    return points.toString();
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankColor = (rank) => {
    if (rank === 1) return '#FFD700';
    if (rank === 2) return '#C0C0C0';
    if (rank === 3) return '#CD7F32';
    if (rank <= 10) return '#4CAF50';
    return '#757575';
  };

  if (isLoading) {
    return (
      <div className={`leaderboard-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="loading">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className={`leaderboard-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Header */}
      <div className="leaderboard-header">
        <h1 className="leaderboard-title">
          <span className="title-icon">📊</span>
          Leaderboard
        </h1>
        <p className="leaderboard-subtitle">
          {showFriendsOnly 
            ? `Compete with your friends! (${myFriends.length} friends)` 
            : "Compete with chefs from around the world!"
          }
        </p>
      </div>

             {/* Tab Navigation */}
             <div className="tab-navigation">
               <div className="tab-buttons">
                 <button
                   className={`tab-button ${activeTab === 'all-time' ? 'active' : ''}`}
                   onClick={() => setActiveTab('all-time')}
                 >
                   <span className="tab-icon">🏆</span>
                   All Time
                 </button>
                 <button
                   className={`tab-button ${activeTab === 'monthly' ? 'active' : ''}`}
                   onClick={() => setActiveTab('monthly')}
                 >
                   <span className="tab-icon">📅</span>
                   This Month
                 </button>
               </div>
               
                              {/* View Switcher */}
               <div className="view-switcher">
                 <div className="view-switcher-label">View Leaderboard</div>
                 <div className="view-switcher-buttons" role="group" aria-label="Leaderboard view mode">
                   <button
                     className={`view-switcher-btn ${!showFriendsOnly ? 'active' : ''}`}
                     onClick={() => setShowFriendsOnly(false)}
                     aria-pressed={!showFriendsOnly}
                   >
                     All Players
                   </button>
                   <button
                     className={`view-switcher-btn ${showFriendsOnly ? 'active' : ''}`}
                     onClick={() => {
                        if (!user) {
                          window.dispatchEvent(new CustomEvent('prepRightAuthPrompt', { detail: { message: 'Please register to view friends.' } }));
                          return;
                        }
                        setShowFriendsOnly(true);
                     }}
                     aria-pressed={showFriendsOnly}
                   >
                     Friends
                   </button>
                 </div>
                 <div className="view-switcher-hint">
                   {showFriendsOnly ? 'Showing only you and your friends' : 'Showing everyone globally'}
                 </div>
               </div>
             </div>

      {/* Leaderboard Content */}
      <div className="leaderboard-content">
        {showFriendsOnly && myFriends.length === 0 ? (
          <div className="no-friends-message">
            <div className="no-friends-icon">👥</div>
            <h3>No Friends Yet</h3>
            <p>Add some friends to see your friends leaderboard!</p>
            <p>Go to your profile or the Feed page to find and add friends.</p>
          </div>
        ) : (
          <div className="leaderboard-list">
            {/* Header Row */}
            <div className="leaderboard-header-row">
              <div className="header-rank">Rank</div>
              <div className="header-player">Player</div>
              <div className="header-stats">Stats</div>
            </div>
            
            {getCurrentLeaderboard().map((player, index) => (
            <div
              key={player.id}
              className={`leaderboard-item ${player.id === user?.id ? 'current-user' : ''}`}
            >
              <div className="rank-section">
                <span
                  className="rank-icon"
                  style={{ color: getRankColor(index + 1) }}
                >
                  {getRankIcon(index + 1)}
                </span>
              </div>
              
              <div className="player-section">
                <div className="player-avatar">
                  {player.name?.charAt(0)?.toUpperCase() || '👤'}
                </div>
                <div className="player-info">
                  <div className="player-name">
                    {player.name || 'Anonymous Chef'}
                    {player.id === user?.id && ' (You)'}
                  </div>
                  <div className="player-level">
                    Level {player.level || 1} • {calculateLevel(player.points || 0).name}
                  </div>
                </div>
              </div>
              
              <div className="stats-section">
                <div className="points-stat">
                  {formatPoints(player.points || 0)} points
                </div>
                <div className="streak-stat">
                  🔥 {player.streak || 0} day streak
                </div>
              </div>
            </div>
          ))}
          </div>
        )}

        {/* User's rank display */}
        {getCurrentUserRank() && (
          <div className="user-rank-section">
            <div className="user-rank-card">
              <h3>Your Rank</h3>
              <div className="user-rank-info">
                <div className="user-rank-icon">
                  {getRankIcon(getCurrentUserRank().rank)}
                </div>
                <div className="user-rank-details">
                  <div className="user-rank-position">
                    Rank #{getCurrentUserRank().rank} out of {getCurrentUserRank().totalPlayers}
                  </div>
                  <div className="user-rank-points">
                    {formatPoints(getCurrentUserRank().points)} points
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="info-section">
        <div className="info-card">
          <h3>📈 How Rankings Work</h3>
          <ul>
            <li>Points are earned through daily tasks and activities</li>
            <li>Streak bonuses multiply your daily points</li>
            <li>All-time rankings never reset</li>
            <li>Monthly rankings reset on the 1st of each month</li>
          </ul>
        </div>
        
        <div className="info-card">
          <h3>🏆 Ranking Benefits</h3>
          <ul>
            <li>Top 10 players get special badges</li>
            <li>Monthly winners get exclusive achievements</li>
            <li>Leaderboard updates in real-time</li>
            <li>Compete with friends and global players</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;

